/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { Link } from "react-router-dom";

import { Fab, Tooltip, TextField } from "@material-ui/core";
import { Search, ViewModule } from "@material-ui/icons";
// CT Kit
import Parallax from "components/Parallax/Parallax.jsx";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";

import {
  getCourses,
  getImage,
  findLectures,
  clearLecFound,
  ApricotAction,
} from "client/actions/courses";
import { selectorCourses } from "client/selectors/courseSelector";
import { selectorOwns } from "client/selectors/ownSelector";
import { AllOwns } from "client/reducers/ownReducer";
import { progressCircles } from "components/Form/FormField";
import { buttonYT } from "client/views/Account/WelcomePage";
import { sChannel } from "assets/jss/views/ResStrings";

import {
  CourseActions,
  getCourseActionProps,
} from "client/views/CoursesPage/CourseActions";
import requireCheck from "client/hocs/requireCheck";

import coursePageStyle from "assets/jss/views/coursePage/coursePageStyle.jsx";
import { courseType } from "client/reducers/courseReducer";
const styles = createStyles(coursePageStyle as any);

interface Props extends WithStyles<typeof styles> {
  courses: any[];
  owns: AllOwns;
  lecFound: any[];
  courseImages: {};
  getCourses: ApricotAction;
  getImage: ApricotAction;
  findLectures: ApricotAction;
  clearLecFound: ApricotAction;
  // component props
  classes: any;
  history: any;
  location: any;
}

const CoursesListBase: React.FC<Props> = ({
  getCourses,
  getImage,
  findLectures,
  clearLecFound,
  courses,
  courseImages,
  owns,
  lecFound,
  classes,
  history,
  location,
}: Props) => {
  const [kWords, setKWords] = useState("");
  const [srhText, setSrhText] = useState("");

  useEffect(() => {
    getCourses();

    return () => {
      clearLecFound();
    };
  }, []);

  useEffect(() => {
    if (courses) {
      courses.forEach(({ _id, imageLink }) => {
        if (imageLink && !courseImages[_id]) {
          getImage({ cid: _id, imgPath: imageLink });
        }
      });
    }
  }, [courses]);

  useEffect(() => {
    var kw = "";
    if (location.state) {
      kw = location.state.kw;
      if (kw) {
        findLectures(null, kw);
      }
    }
    setKWords(kw);
    setSrhText(kw);
  }, [location.state]);

  const renderSearch = () => {
    const srhTextId = "_lecSearchKWords";
    const showLecFound = kWords !== "" && lecFound ? true : false;
    return (
      <div style={{ width: "95%", margin: "2.625rem auto 1rem", padding: 0 }}>
        <TextField
          className={classes.srhText}
          id={srhTextId}
          label="Search"
          type="search"
          color="secondary"
          value={srhText}
          onChange={(e) => setSrhText(e.target.value)}
        />
        <Tooltip title="Find Lectures">
          <span>
            <Fab
              style={{ margin: "auto 0.5rem" }}
              disabled={srhText === "" || srhText === kWords}
              onClick={submitSearch}
              size="medium"
              color="secondary">
              <Search />
            </Fab>
          </span>
        </Tooltip>
        <Tooltip title="Clear Search">
          <span>
            <Fab
              style={{ margin: "auto 0.5rem" }}
              disabled={!showLecFound}
              onClick={showAll}
              size="medium"
              color="secondary">
              <ViewModule />
            </Fab>
          </span>
        </Tooltip>
        <div className={classes.btnYT1}>
          {buttonYT(false, sChannel, "Our Channel")}
        </div>
      </div>
    );
  };

  const renderLecFound = () => {
    const lecs = lecFound.map((crsFound: any, count: number) => {
      const courseIdx = courses.findIndex((crs) => crs._id === crsFound._id);
      const { _id, title, category, imageLink } = courses[courseIdx];
      if (!courseImages[_id]) {
        getImage({ cid: _id, imgPath: imageLink });
      }
      return (
        <div
          style={{
            backgroundImage: courseImages[_id]
              ? `url(data:image/jpeg;base64,${courseImages[_id]})`
              : ``,
          }}
          key={_id}
          className={classes.crsFoundCard}>
          <div className={classes.crsFoundCardHead}>
            <div className={classes.crsTitle}>
              <h6>{category}</h6>
              <h2>{title}</h2>
            </div>
            {renderActions(courses[courseIdx])}
          </div>
          <div className={classes.crsFoundCardBody}>
            <ul>
              {crsFound.lectures.map((lec: any) => (
                <li key={lec._id}>
                  <Link to={`/clsrm/${crsFound._id}/${lec._id}`}>
                    <h3 style={{ margin: "0 1rem ", color: "white" }}>
                      <span style={{ fontFamily: "Open Sans" }}>
                        {lec.seq + 1}.&nbsp;
                      </span>
                      {lec.title}
                    </h3>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    });

    return (
      <>
        <h4 style={{ width: "95%", margin: "0 auto 0.75rem", padding: 0 }}>
          {lecFound.length ? (
            <span>
              Lectures related to <b>{kWords}</b> :
            </span>
          ) : (
            "There is no lectures related to the keyword(s)."
          )}
        </h4>
        {lecs}
      </>
    );
  };

  const renderActions = (course: any) => {
    const { info } = getCourseActionProps(owns, course, false);
    return (
      <CourseActions
        inCard={true}
        info={info}
        history={history}
        // price={price}
      />
    );
  };

  const renderCourses = () =>
    courses.map(
      (course: any, idx: number) =>
        course.ctype === courseType.COURSE && (
          <GridItem xs={12} sm={12} md={6} key={course._id}>
            <Card
              lectures
              background
              style={{
                backgroundImage: courseImages[course._id]
                  ? `url(data:image/jpeg;base64,${courseImages[course._id]})`
                  : ``,
              }}>
              <CardBody background className={classes.crsListCard}>
                <h6 className={classes.crsHead}>{course.category}</h6>
                <h2
                  style={{ cursor: "pointer", padding: "0 1rem" }}
                  onClick={() => history.push(`/crp/${course._id}`)}>
                  {course.title}
                </h2>
                <div className={classes.crsFooter}>{renderActions(course)}</div>
              </CardBody>
            </Card>
          </GridItem>
        ),
    );

  const submitSearch = () => {
    if (srhText.length) {
      setKWords(srhText);
      history.push({
        pathname: "/cr_list",
        state: { kw: srhText },
      });
    }
  };

  const showAll = () => {
    clearLecFound();
    history.push("/cr_list");
  };

  const renderBody = () => {
    if (courses.length === 0 || !owns) {
      return <div style={{ width: "100%" }}>{progressCircles()}</div>;
    } else {
      return kWords !== "" && lecFound ? renderLecFound() : renderCourses();
    }
  };

  return (
    <>
      <Parallax
        image={require("assets/img/classroom1.jpg")}
        filter="courses"
        banner>
        <div className={classes.container}>
          <GridContainer>
            <GridItem md={8} sm={8} className={classes.mrAuto}>
              <div className={classes.brand}>
                <h1 className={classes.title}>Courses</h1>
              </div>
            </GridItem>
          </GridContainer>
        </div>
        <div className={classes.btnYT2}>
          {buttonYT(false, sChannel, "Our Channel")}
        </div>
      </Parallax>

      <div className={classes.pageContent}>
        <div className={classes.container}>
          <GridContainer>
            {renderSearch()}
            {renderBody()}
          </GridContainer>
        </div>
      </div>
    </>
  );
};

function mapStateToProps(state: any) {
  return {
    courses: selectorCourses(state),
    courseImages: state.state_course.courseImages,
    lecFound: state.state_course.lecFound,
    owns: selectorOwns(state),
  };
}

function loadData(store: any) {
  return store.dispatch(getCourses(""));
}

const base: any = CoursesListBase;
export const CoursesListPage = {
  loadData, // loadData: loadData
  component: requireCheck(
    withStyles(styles)(
      connect(mapStateToProps, {
        getCourses,
        getImage,
        findLectures,
        clearLecFound,
      })(base),
    ),
    { loginRequired: false },
  ),
};

export const CoursesListPageTest = withStyles(styles)(CoursesListBase);
